import React, { useState, useEffect } from 'react';
import { useAuth } from '../Auth/AuthProvider';
import { updateProfile } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { quizService } from '../../services/quizService';
import logger from '../../utils/logger';
import './MyPage.css';

const MyPage = ({ onBack }) => {
  const { user, logout } = useAuth();
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [jwtTestResult, setJwtTestResult] = useState(null);
  const [jwtTesting, setJwtTesting] = useState(false);
  const [quizGenResult, setQuizGenResult] = useState(null);
  const [quizGenerating, setQuizGenerating] = useState(false);
  const [sharedQuizAttempts, setSharedQuizAttempts] = useState([]);
  const [loadingSharedAttempts, setLoadingSharedAttempts] = useState(false);
  const [showSharedAttempts, setShowSharedAttempts] = useState(false);

  // Load shared quiz attempts when component mounts
  useEffect(() => {
    if (user && showSharedAttempts && !loadingSharedAttempts && sharedQuizAttempts.length === 0) {
      loadSharedQuizAttempts();
    }
  }, [user, showSharedAttempts]);

  const loadSharedQuizAttempts = async () => {
    if (!user) return;
    
    setLoadingSharedAttempts(true);
    try {
      logger.info('Loading shared quiz attempts for user', { userId: user.uid }, 'COMPONENT');
      const attempts = await quizService.getSharedQuizAttempts(user.uid);
      setSharedQuizAttempts(attempts);
      logger.info(`Loaded ${attempts.length} shared quiz attempts`, { 
        userId: user.uid, 
        attemptCount: attempts.length 
      }, 'COMPONENT');
    } catch (error) {
      logger.error('Failed to load shared quiz attempts', {
        userId: user.uid,
        error: error.message
      }, 'COMPONENT');
      alert('公開クイズ履歴の読み込みに失敗しました');
    } finally {
      setLoadingSharedAttempts(false);
    }
  };

  const handleShowSharedAttempts = () => {
    setShowSharedAttempts(!showSharedAttempts);
    if (!showSharedAttempts) {
      loadSharedQuizAttempts();
    }
  };

  const handleSave = async () => {
    if (!displayName.trim()) {
      alert('ディスプレイ名を入力してください');
      return;
    }

    setSaving(true);
    try {
      // Firebase AuthenticationのdisplayNameを更新
      await updateProfile(user, {
        displayName: displayName.trim()
      });
      
      // FirestoreのusersコレクションのdisplayNameも更新
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        displayName: displayName.trim()
      });
      
      setIsEditing(false);
      alert('ディスプレイ名を更新しました');
    } catch (error) {
      console.error('Failed to update display name:', error);
      alert('ディスプレイ名の更新に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDisplayName(user?.displayName || '');
    setIsEditing(false);
  };

  const testJwtAuthentication = async () => {
    if (!user) {
      alert('ログインしてください');
      return;
    }

    setJwtTesting(true);
    setJwtTestResult(null);

    try {
      // Firebase JWTトークンを取得
      const token = await user.getIdToken();

      // jwt_test functionを呼び出し
      const response = await fetch('https://asia-northeast1-quizenie-5cf54.cloudfunctions.net/jwt_test', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      setJwtTestResult({
        status: response.status,
        data: result
      });

    } catch (error) {
      console.error('JWT test error:', error);
      setJwtTestResult({
        status: 500,
        data: {
          success: false,
          error: 'NETWORK_ERROR',
          message: 'ネットワークエラーが発生しました',
          details: error.message
        }
      });
    } finally {
      setJwtTesting(false);
    }
  };

  const testQuizGeneration = async () => {
    if (!user) {
      alert('ログインしてください');
      return;
    }

    setQuizGenerating(true);
    setQuizGenResult(null);

    try {
      // Firebase JWTトークンを取得
      const token = await user.getIdToken();

      // helloWorldRich functionを呼び出し
      const response = await fetch('https://helloworldrich-yvlpoybffq-an.a.run.app', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      setQuizGenResult({
        status: response.status,
        data: result
      });

    } catch (error) {
      console.error('Quiz generation test error:', error);
      setQuizGenResult({
        status: 500,
        data: {
          success: false,
          error: 'NETWORK_ERROR',
          message: 'ネットワークエラーが発生しました',
          details: error.message
        }
      });
    } finally {
      setQuizGenerating(false);
    }
  };

  return (
    <div className="my-page">
      <div className="my-page-header">
        <h2>マイページ</h2>
        <button onClick={onBack} className="back-btn">
          ← 戻る
        </button>
      </div>

      <div className="my-page-content">
        <div className="profile-section">
          <div className="profile-card">
            <h3>プロフィール</h3>
            
            <div className="profile-item">
              <label>メールアドレス</label>
              <div className="profile-value">{user?.email}</div>
            </div>

            <div className="profile-item">
              <label>ディスプレイ名</label>
              {isEditing ? (
                <div className="edit-display-name">
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="ディスプレイ名を入力"
                    maxLength={50}
                  />
                  <div className="edit-actions">
                    <button 
                      onClick={handleSave}
                      disabled={saving}
                      className="save-btn"
                    >
                      {saving ? '保存中...' : '保存'}
                    </button>
                    <button 
                      onClick={handleCancel}
                      disabled={saving}
                      className="cancel-btn"
                    >
                      キャンセル
                    </button>
                  </div>
                </div>
              ) : (
                <div className="display-name-view">
                  <span className="profile-value">
                    {user?.displayName || '未設定'}
                  </span>
                  <button 
                    onClick={() => setIsEditing(true)}
                    className="edit-btn"
                  >
                    編集
                  </button>
                </div>
              )}
            </div>

            <div className="profile-item">
              <label>ユーザーID</label>
              <div className="profile-value user-id">{user?.uid}</div>
            </div>

            <div className="profile-item">
              <label>アカウント作成日</label>
              <div className="profile-value">
                {user?.metadata?.creationTime 
                  ? new Date(user.metadata.creationTime).toLocaleDateString('ja-JP')
                  : '不明'
                }
              </div>
            </div>
          </div>
        </div>

        <div className="account-section">
          <div className="account-card">
            <h3>アカウント統計</h3>
            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-label">作成したクイズ</div>
                <div className="stat-value">準備中</div>
              </div>
              <div className="stat-item">
                <div className="stat-label">解答した問題</div>
                <div className="stat-value">準備中</div>
              </div>
              <div className="stat-item">
                <div className="stat-label">正解率</div>
                <div className="stat-value">準備中</div>
              </div>
            </div>
          </div>
        </div>

        <div className="shared-quiz-attempts-section">
          <div className="shared-quiz-attempts-card">
            <h3>公開クイズ回答履歴</h3>
            <p>他のユーザーが公開したクイズに回答した履歴を確認できます。</p>
            
            <button 
              onClick={handleShowSharedAttempts}
              className="show-attempts-btn"
              disabled={loadingSharedAttempts}
            >
              {loadingSharedAttempts ? '読み込み中...' : 
               showSharedAttempts ? '履歴を隠す' : '履歴を表示'}
            </button>

            {showSharedAttempts && (
              <div className="attempts-list">
                {sharedQuizAttempts.length === 0 ? (
                  <div className="no-attempts">
                    <p>まだ公開クイズに回答していません。</p>
                    <button 
                      onClick={() => window.location.href = '/public'}
                      className="go-to-public-btn"
                    >
                      公開クイズ一覧を見る
                    </button>
                  </div>
                ) : (
                  <div className="attempts-grid">
                    {sharedQuizAttempts.map((attempt) => (
                      <div key={attempt.id} className="attempt-card">
                        <div className="attempt-header">
                          <h4 className="quiz-title">{attempt.quizTitle}</h4>
                          <div className="attempt-date">
                            {new Date(attempt.finishedAt).toLocaleDateString('ja-JP', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                        
                        <div className="attempt-details">
                          <div className="score-info">
                            <span className="score">
                              {attempt.score}/{attempt.answers?.length || 0} 問正解
                            </span>
                            <span className="percentage">
                              ({Math.round((attempt.score / (attempt.answers?.length || 1)) * 100)}%)
                            </span>
                          </div>
                          
                          {attempt.quizDescription && (
                            <div className="quiz-description">
                              {attempt.quizDescription}
                            </div>
                          )}
                          
                          <div className="attempt-meta">
                            <span className="quiz-author">
                              作成者ID: {attempt.originalUserId}
                            </span>
                          </div>
                        </div>

                        <div className="attempt-actions">
                          <button 
                            onClick={() => window.location.href = `/shared-quiz/${attempt.originalUserId}/${attempt.quizId}`}
                            className="retry-quiz-btn"
                          >
                            再挑戦
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="logout-section">
          <div className="logout-card">
            <h3>アカウント操作</h3>
            <button onClick={logout} className="logout-btn">
              ログアウト
            </button>
          </div>
        </div>

        {/* 開発環境でのみ表示 */}
        {process.env.NODE_ENV === 'development' && (
          <div className="jwt-test-section">
            <div className="jwt-test-card">
              <h3>JWT認証テスト (開発用)</h3>
              <p>Firebase JWTトークンを使用してAPI認証をテストします</p>
            
            <button 
              onClick={testJwtAuthentication}
              disabled={jwtTesting}
              className="jwt-test-btn"
            >
              {jwtTesting ? 'テスト中...' : 'JWT認証テスト実行'}
            </button>

            {jwtTestResult && (
              <div className={`jwt-test-result ${jwtTestResult.data.success ? 'success' : 'error'}`}>
                <h4>テスト結果 (Status: {jwtTestResult.status})</h4>
                <div className="result-content">
                  <div className="result-item">
                    <strong>認証結果:</strong> {jwtTestResult.data.success ? '✅ 成功' : '❌ 失敗'}
                  </div>
                  <div className="result-item">
                    <strong>メッセージ:</strong> {jwtTestResult.data.message}
                  </div>
                  
                  {/* ユーザー情報は成功時のみ表示 */}
                  {jwtTestResult.data.success && jwtTestResult.data.user && (
                    <div className="user-info">
                      <h5>🔍 認証されたユーザー情報:</h5>
                      <div className="result-item">
                        <strong>ユーザーID (UID):</strong> {jwtTestResult.data.user.uid}
                      </div>
                      <div className="result-item">
                        <strong>メールアドレス:</strong> {jwtTestResult.data.user.email}
                      </div>
                      <div className="result-item">
                        <strong>メール認証状態:</strong> {jwtTestResult.data.user.email_verified ? '✅ 認証済み' : '❌ 未認証'}
                      </div>
                      <div className="result-item">
                        <strong>認証プロバイダー:</strong> {jwtTestResult.data.user.firebase?.sign_in_provider || '不明'}
                      </div>
                      {jwtTestResult.data.user.firebase?.identities && (
                        <div className="result-item">
                          <strong>アイデンティティ:</strong> {JSON.stringify(jwtTestResult.data.user.firebase.identities)}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* 現在ログイン中のユーザー情報も参考として表示 */}
                  <div className="current-user-info">
                    <h5>📋 現在ログイン中のユーザー (参考):</h5>
                    <div className="result-item">
                      <strong>ローカルUID:</strong> {user?.uid || '不明'}
                    </div>
                    <div className="result-item">
                      <strong>ローカルEmail:</strong> {user?.email || '不明'}
                    </div>
                    <div className="result-item">
                      <strong>表示名:</strong> {user?.displayName || '未設定'}
                    </div>
                  </div>

                  {jwtTestResult.data.token_info && (
                    <div className="token-info">
                      <h5>トークン情報:</h5>
                      <div className="result-item">
                        <strong>発行者:</strong> {jwtTestResult.data.token_info.iss}
                      </div>
                      <div className="result-item">
                        <strong>発行時刻:</strong> {jwtTestResult.data.token_info.iat}
                      </div>
                      <div className="result-item">
                        <strong>有効期限:</strong> {jwtTestResult.data.token_info.exp}
                      </div>
                    </div>
                  )}

                  {!jwtTestResult.data.success && (
                    <div className="error-info">
                      <div className="result-item">
                        <strong>エラーコード:</strong> {jwtTestResult.data.error}
                      </div>
                      {jwtTestResult.data.details && (
                        <div className="result-item">
                          <strong>詳細:</strong> {jwtTestResult.data.details}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="result-item">
                    <strong>検証時刻:</strong> {jwtTestResult.data.verified_at}
                  </div>
                </div>
              </div>
            )}
            </div>
          </div>
        )}

        {/* 開発環境でのみ表示 */}
        {process.env.NODE_ENV === 'development' && (
          <div className="quiz-gen-test-section">
            <div className="quiz-gen-test-card">
              <h3>AI クイズ生成テスト (JWT認証対応) (開発用)</h3>
              <p>JWT認証を使用してAIクイズ生成APIをテストします</p>
            
            <button 
              onClick={testQuizGeneration}
              disabled={quizGenerating}
              className="quiz-gen-test-btn"
            >
              {quizGenerating ? 'クイズ生成中...' : 'AIクイズ生成テスト実行'}
            </button>

            {quizGenResult && (
              <div className={`quiz-gen-result ${quizGenResult.data.success ? 'success' : 'error'}`}>
                <h4>クイズ生成結果 (Status: {quizGenResult.status})</h4>
                <div className="result-content">
                  <div className="result-item">
                    <strong>成功:</strong> {quizGenResult.data.success ? '✅ はい' : '❌ いいえ'}
                  </div>
                  <div className="result-item">
                    <strong>メッセージ:</strong> {quizGenResult.data.message}
                  </div>
                  
                  {quizGenResult.data.success && quizGenResult.data.authenticated_user && (
                    <div className="auth-user-info">
                      <h5>🔒 認証されたユーザー:</h5>
                      <div className="result-item">
                        <strong>UID:</strong> {quizGenResult.data.authenticated_user.uid}
                      </div>
                      <div className="result-item">
                        <strong>Email:</strong> {quizGenResult.data.authenticated_user.email}
                      </div>
                    </div>
                  )}

                  {quizGenResult.data.success && quizGenResult.data.quiz_data && (
                    <div className="quiz-data-info">
                      <h5>📝 生成されたクイズ:</h5>
                      <div className="result-item">
                        <strong>問題数:</strong> {quizGenResult.data.quiz_data.length}問
                      </div>
                      {quizGenResult.data.quiz_data.map((question, index) => (
                        <div key={index} className="question-preview">
                          <h6>問題 {index + 1}:</h6>
                          <div className="result-item">
                            <strong>質問:</strong> {question.questionText}
                          </div>
                          <div className="result-item">
                            <strong>種類:</strong> {question.questionType === 'single' ? '単一選択' : '複数選択'}
                          </div>
                          <div className="result-item">
                            <strong>選択肢数:</strong> {question.choices?.length || 0}個
                          </div>
                          {question.tags && question.tags.length > 0 && (
                            <div className="result-item">
                              <strong>タグ:</strong> {question.tags.join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {!quizGenResult.data.success && (
                    <div className="error-info">
                      <div className="result-item">
                        <strong>エラーコード:</strong> {quizGenResult.data.error}
                      </div>
                      {quizGenResult.data.details && (
                        <div className="result-item">
                          <strong>詳細:</strong> {quizGenResult.data.details}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="result-item">
                    <strong>実行時刻:</strong> {quizGenResult.data.generated_at || new Date().toISOString()}
                  </div>
                </div>
              </div>
            )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyPage;